import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import Button from '@jobcase/react_pattern_library/components/Button';
import Typography from '@jobcase/react_pattern_library/components/Typography/TypographyRebrand';
import Spinner from '@jobcase/react_pattern_library/components/Spinner';
import styles from '@jobcase/react_pattern_library/styles/rebrand';

import { routeToEmployerCenterToOrganization } from '../../../../redux/nodes/router/actions';
import { getNewOrganizationId } from '../../../../redux/nodes/entities/subOrganizations/selectors';

import { useSelector } from '../../../../utils/hooks';
import { reportOnboardingContinueToEmployerCenter } from '../../../../utils/reporting/employerOnboardingEvents';

const HeadingTypography = styled(Typography)`
  font-size: ${styles.font.fontSize.h1Mobile};
  font-weight: 500;
  line-height: 28px;
  margin: ${({ isEditMode }) => (isEditMode ? `0 0  ${styles.spacing.medium}px`
    : `${styles.spacing.small}px 0 ${styles.spacing.tiny}px 0`)};
  margin-top: ${({ isLargeOrg }) => (isLargeOrg && `${styles.spacing.extraTiny}px`)};
  max-width: ${({ isLargeOrg }) => (isLargeOrg && '336px')};

  @media (min-width: ${styles.breakpoint.md}) {
    font-size: ${styles.font.fontSize.h1};
    line-height: ${styles.font.lineHeight.h1Mobile};
    margin-top: 0;
    max-width: ${({ isLargeOrg }) => (isLargeOrg && 'unset')};
  }
`;

const StyledTypography = styled(Typography)`
  color: ${styles.color.textGrayColor};
  line-height: ${styles.font.lineHeight.body2};
  margin: 0 0  ${styles.spacing.medium}px;
`;

const ConfirmationTypography = styled(Typography)`
  color: ${styles.color.textGrayColor};
  line-height: ${styles.font.lineHeight.body2};
  margin: 0 0 ${styles.spacing.medium}px;
`;

const ConfirmationScreen = styled.div`
  display: block;
`;

const ConfirmationScreenIcon = styled.div`
  display: flex;
  margin-top: ${styles.spacing.small}px;
  width: 100%;

  @media screen and (min-width: ${styles.breakpoint.md}) {
    margin-top: 0;
  }
`;

const ConfirmationStyledButton = styled(Button)`
  margin-top: ${styles.spacing.tiny}px;
  padding: 0;
  width: 100%;

  button {
    border: 0;
  }

  @media screen and (min-width: ${styles.breakpoint.md}) {
    min-width: 288px;
    width: auto;
  }

  ${({ isLoading }) => isLoading && css`
    pointer-events: none;
  `}
`;

const StepWrapper = styled.div`
  display: flex;
  margin-bottom: ${styles.spacing.tiny}px;
`;

const StepImageWrapper = styled.div`
  align-items: flex-start;
  display: flex;
`;

const StepImage = styled.img`
  margin-right: ${styles.spacing.tiny}px;
`;

const StepTypographyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const SubHeadingTypography = styled(Typography)`
  line-height: 22px;
  margin-bottom: ${styles.spacing.medium}px;
`;

const StepHeadingTypography = styled(Typography)`
  line-height: ${styles.font.lineHeight.body2};
  margin-bottom: ${styles.spacing.extraTiny + 1}px;
  max-width: 242px;

  @media (min-width: ${styles.breakpoint.md}) {
    line-height: 17px;
    max-width: 100%;
  }
`;

const StepTypography = styled(Typography)`
  color: ${styles.color.textGrayColor};
  line-height: ${styles.font.lineHeight.body2};
  max-width: 242px;

  @media (min-width: ${styles.breakpoint.md}) {
    max-width: 599px;
  }
`;

const OnboardingConfirmation = ({ isLargeOrg, isPartnerUser }) => {
  const organizationId = useSelector(getNewOrganizationId);
  const [isLoading, setIsLoading] = useState(false);

  const handleOnContinueToEmployerCenter = useCallback(() => {
    setIsLoading(true);
    reportOnboardingContinueToEmployerCenter();
    routeToEmployerCenterToOrganization(organizationId);
  }, [organizationId]);

  return (
    <ConfirmationScreen>
      {/*
        * LUT-524: partner requests do not get platform access until they are approved
        * (the Employer Center shows an account-under-review page instead), so the
        * confirmation must not promise hubs, candidates, or a working employer center.
        */}
      {isPartnerUser && (
        <>
          <ConfirmationScreenIcon>
            <img src="/static-ec/onboarding/large-org-icon.svg" alt="check icon" />
          </ConfirmationScreenIcon>
          <HeadingTypography variant={Typography.VARIANTS.H1} isLargeOrg>Thanks for signing up, your account is under review!</HeadingTypography>
          <ConfirmationTypography variant={Typography.VARIANTS.BODY}>
            Your partner account request was created. Our team is reviewing it, and you will be able to access the partner tools as soon as it is approved.
          </ConfirmationTypography>
        </>
      )}
      {!isLargeOrg && !isPartnerUser && (
        <>
          <HeadingTypography variant={Typography.VARIANTS.H1}>Welcome to the Jobcase Employer Center!</HeadingTypography>
          <StyledTypography variant={Typography.VARIANTS.BODY}>
            Your account was created and you’re one step closer to hiring the workers you need. You can now create a hub and manage candidates.
          </StyledTypography>
          <SubHeadingTypography variant={Typography.VARIANTS.H2}>
            What’s next?
          </SubHeadingTypography>
          <div>
            <StepWrapper>
              <StepImageWrapper>
                <StepImage src="/static-ec/onboarding/reviewing-org.svg" alt="step icon 1" />
              </StepImageWrapper>
              <StepTypographyWrapper>
                <StepHeadingTypography variant={Typography.VARIANTS.H3}>
                  Jobcase is reviewing your organization for approval
                </StepHeadingTypography>
                <StepTypography variant={Typography.VARIANTS.BODY}>
                  This process usually takes 1 business day.
                </StepTypography>
              </StepTypographyWrapper>
            </StepWrapper>
          </div>
          <ConfirmationStyledButton
            noOuterPadding
            onClick={handleOnContinueToEmployerCenter}
            isLoading={isLoading}
            isDisabled={isLoading}
          >
            {isLoading ? <Spinner color={styles.color.jobcaseWhiteColor} /> : 'Continue to employer center'}
          </ConfirmationStyledButton>
        </>
      )}
      {isLargeOrg && !isPartnerUser && (
        <>
          <ConfirmationScreenIcon>
            <img src="/static-ec/onboarding/large-org-icon.svg" alt="check icon" />
          </ConfirmationScreenIcon>
          <HeadingTypography variant={Typography.VARIANTS.H1} isLargeOrg={isLargeOrg}>Welcome to Jobcase, we’ll be in touch!</HeadingTypography>
          <ConfirmationTypography variant={Typography.VARIANTS.BODY}>
            Thanks for filling out your account details, our customer success team will reach out using the contact information you provided.
          </ConfirmationTypography>
        </>
      )}
    </ConfirmationScreen>
  );
};

OnboardingConfirmation.propTypes = {
  isLargeOrg: PropTypes.bool,
  isPartnerUser: PropTypes.bool,
};

OnboardingConfirmation.defaultProps = {
  isLargeOrg: null,
  isPartnerUser: false,
};

export default OnboardingConfirmation;
